import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";

export const EPLANNER_OFFSET_UNITS = ["Day", "Week", "Month"];
const MAX_EPLANNER_OFFSETS = 12;

export const defaultEPlannerOffset = () => ({
  key: `offset-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  amount: "1",
  unit: "Day",
});

const normalizeUnit = (unit) => {
  const value = String(unit || "").trim();
  if (/^week$/i.test(value)) return "Week";
  if (/^month$/i.test(value)) return "Month";
  return "Day";
};

export const maxAmountForEPlannerUnit = (unit) => {
  const value = normalizeUnit(unit);
  if (value === "Week") return 52;
  if (value === "Month") return 12;
  return 365;
};

export const ePlannerOffsetDurationDays = (amount, unit) => {
  const value = Number(amount);
  const normalized = normalizeUnit(unit);
  if (normalized === "Week") return value * 7;
  if (normalized === "Month") return value * 30;
  return value;
};

export const ePlannerOffsetExceedsOneYear = (amount, unit) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return true;
  return value > maxAmountForEPlannerUnit(unit);
};

export const nextEPlannerOffset = (existing) => {
  const rows = Array.isArray(existing) ? existing : [];
  const usedKeys = new Set(
    rows.map((row) => `${Number(row.amount)}|${normalizeUnit(row.unit)}`)
  );
  const usedDays = new Set(
    rows.map((row) => ePlannerOffsetDurationDays(row.amount, row.unit))
  );
  for (let amount = 1; amount <= 365; amount += 1) {
    if (!usedKeys.has(`${amount}|Day`) && !usedDays.has(amount)) {
      return {
        key: `offset-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        amount: String(amount),
        unit: "Day",
      };
    }
  }
  return defaultEPlannerOffset();
};

export const fetchOrganizationReminderSettings = async (organizationId) => {
  const url = `${API_ENDPOINTS.REMINDERS.GET_ORG_SETTINGS}?organizationId=${encodeURIComponent(organizationId)}`;
  return api.get(url);
};

export const saveOrganizationReminderSettings = async (payload) => {
  return api.post(API_ENDPOINTS.REMINDERS.ORG_SETTINGS_ACTIVITY, payload);
};

export const parseEPlannerOffsets = (data) => {
  const raw = data?.EPlannerOffsets || data?.ePlannerOffsets || [];
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item, index) => ({
      key: item.OffsetId || item.offsetId || `offset-${index}`,
      amount: String(item.Amount ?? item.amount ?? 1),
      unit: normalizeUnit(item.Unit || item.unit),
    }));
  }

  const fallbackDays = data?.EPlannerDaysBefore ?? data?.ePlannerDaysBefore;
  return [
    {
      key: "offset-default",
      amount: fallbackDays != null && fallbackDays !== "" ? String(fallbackDays) : "1",
      unit: "Day",
    },
  ];
};

export const validateOrganizationReminderSettingsForm = (form) => {
  if (form?.remindersEnabled) {
    const runTime = String(form.reminderDailyRunTime ?? "").trim();
    if (!runTime) {
      return {
        valid: false,
        field: "reminderDailyRunTime",
        title: "Daily run time required",
        message: "Please enter the daily run time when reminders are enabled.",
      };
    }

    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(runTime)) {
      return {
        valid: false,
        field: "reminderDailyRunTime",
        title: "Invalid daily run time",
        message: "Daily run time must be in HH:mm format.",
      };
    }
  }

  const offsets = Array.isArray(form.ePlannerOffsets) ? form.ePlannerOffsets : [];
  if (!form.allowMultipleEPlannerReminders && offsets.length > 1) {
    return {
      valid: false,
      field: "ePlannerOffsets",
      title: "Multiple points not allowed",
      message: "Turn on multiple reminder points before adding more than one ePlanner reminder.",
    };
  }

  if (form?.remindersEnabled && offsets.length === 0) {
    return {
      valid: false,
      field: "ePlannerOffsets",
      title: "Reminder point required",
      message: "Add at least one ePlanner reminder point when reminders are on.",
    };
  }

  if (offsets.length > MAX_EPLANNER_OFFSETS) {
    return {
      valid: false,
      field: "ePlannerOffsets",
      title: "Too many reminder points",
      message: "You can add up to 12 ePlanner reminder points.",
    };
  }

  const seenKeys = new Set();
  const seenDays = new Set();
  for (let i = 0; i < offsets.length; i += 1) {
    const amountRaw = String(offsets[i]?.amount ?? "").trim();
    if (!/^\d+$/.test(amountRaw)) {
      return {
        valid: false,
        field: "ePlannerOffsets",
        title: "Invalid reminder point",
        message: "Each reminder point must be a whole number.",
      };
    }
    const amount = Number(amountRaw);
    const unit = normalizeUnit(offsets[i]?.unit);
    if (amount < 1) {
      return {
        valid: false,
        field: "ePlannerOffsets",
        title: "Invalid reminder point",
        message: "Each reminder point must be at least 1.",
      };
    }
    if (ePlannerOffsetExceedsOneYear(amount, unit)) {
      return {
        valid: false,
        field: "ePlannerOffsets",
        title: "Reminder too far ahead",
        message: "An appointment reminder cannot be more than 1 year before the appointment.",
      };
    }
    const key = `${amount}|${unit}`;
    const durationDays = ePlannerOffsetDurationDays(amount, unit);
    if (seenKeys.has(key) || seenDays.has(durationDays)) {
      return {
        valid: false,
        field: "ePlannerOffsets",
        title: "Duplicate reminder point",
        message: "This e-Planner reminder point already exists.",
      };
    }
    seenKeys.add(key);
    seenDays.add(durationDays);
  }

  return { valid: true };
};

export const parseOrganizationReminderSettings = (response) => {
  const defaults = {
    ok: true,
    remindersEnabled: false,
    reminderDailyRunTime: "09:00",
    allowMultipleEPlannerReminders: false,
    ePlannerOffsets: [defaultEPlannerOffset()],
    timeZoneId: "GMT Standard Time",
    reminderEngineLastHeartbeat: null,
  };

  if (!response || response.success !== 1) {
    const message = String(response?.message || "").trim();
    if (!message) return defaults;
    return { ok: false, message: message || "Could not load reminder settings." };
  }

  const data = response.data || {};
  const rawTime = data.ReminderDailyRunTime || "09:00:00";
  const timeParts = String(rawTime).split(":");
  const hhmm =
    timeParts.length >= 2
      ? `${String(timeParts[0]).padStart(2, "0")}:${String(timeParts[1]).padStart(2, "0")}`
      : "09:00";

  return {
    ok: true,
    remindersEnabled: Boolean(data.RemindersEnabled),
    reminderDailyRunTime: hhmm,
    allowMultipleEPlannerReminders: Boolean(
      data.AllowMultipleEPlannerReminders ?? data.allowMultipleEPlannerReminders
    ),
    ePlannerOffsets: parseEPlannerOffsets(data),
    timeZoneId: data.TimeZoneId || "GMT Standard Time",
    reminderEngineLastHeartbeat: data.ReminderEngineLastHeartbeat || null,
  };
};

export const getReminderSettingsSuccessMessage = () =>
  "Reminder settings have been updated. The Reminder Worker uses these settings for automatic dispatch.";
